import React, { useState, useEffect, useMemo } from "react";
import { modalBoxStyle } from "../styles";
import {
  Typography,
  Box,
  Modal,
  Grid,
  IconButton,
  TextField,
  Button,
  CircularProgress,
  Alert,
  List,
  ListItem,
} from "@mui/material";
import CloseOutlinedIcon from "@mui/icons-material/CloseOutlined";
import { useMutation, useQueryClient } from "react-query";
import inventoryClient from "../../../clients/inventoryClient";

const sanitize = (value) => value.replace(/[^0-9.,]/g, "");

const toRowState = (records) =>
  records.reduce((acc, r) => {
    acc[r.id] = r.quantityFound != null ? r.quantityFound.toString() : "";
    return acc;
  }, {});

const EditStockCountRecordQuantityModal = ({
  isOpen,
  handleCloseEditQuantityModal,
  records,
  product,
  stockCountId,
  establishmentId,
  onSuccessCallback,
}) => {
  const [quantityById, setQuantityById] = useState({});
  const queryClient = useQueryClient();

  const recordList = useMemo(() => records || [], [records]);
  const initialState = useMemo(() => toRowState(recordList), [recordList]);

  useEffect(() => {
    if (isOpen) {
      setQuantityById(initialState);
    }
  }, [isOpen, initialState]);

  const patchMutation = useMutation(
    inventoryClient.patchCompletedStockCountRecords,
    {
      onSuccess: () => {
        queryClient.invalidateQueries([
          "stock-count",
          { establishmentId, stockCountId },
        ]);
        closeModal();
        if (onSuccessCallback) onSuccessCallback();
      },
      onError: (error) => {
        console.error("Error updating stock count records:", error);
      },
    }
  );

  const handleQuantityChange = (id, value) => {
    setQuantityById((prev) => ({ ...prev, [id]: sanitize(value) }));
  };

  const closeModal = () => {
    handleCloseEditQuantityModal();
    setQuantityById({});
  };

  const dirtyRecords = recordList.filter(
    (r) => (quantityById[r.id] ?? "") !== (initialState[r.id] ?? "")
  );

  const allValid = recordList.every((r) => {
    const v = quantityById[r.id];
    return v !== undefined && v !== "" && parseFloat(v) >= 0;
  });

  const canSave = dirtyRecords.length > 0 && allValid;

  const handleSave = () => {
    if (!canSave) return;
    const payload = dirtyRecords.map((r) => ({
      recordId: r.id,
      quantityFound: parseFloat(quantityById[r.id]),
    }));
    patchMutation.mutate({
      establishmentId,
      stockCountId,
      records: payload,
    });
  };

  const isSingle = recordList.length === 1;

  return (
    <Modal
      open={isOpen}
      onClose={closeModal}
      aria-labelledby="edit-quantity-modal"
      aria-describedby="edit-stock-count-record-quantity"
    >
      <Box sx={modalBoxStyle}>
        {patchMutation.isLoading ? (
          <Grid
            container
            direction="row"
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              minHeight: "200px",
            }}
          >
            <CircularProgress />
          </Grid>
        ) : (
          <>
            <Grid
              container
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: 4,
              }}
            >
              <Grid item sx={{ minWidth: 0 }}>
                <Typography variant="h4">
                  {product?.productName || "Product Name"}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {product?.formatName || "Product Format"}
                </Typography>
              </Grid>
              <Grid item>
                <IconButton
                  sx={{ backgroundColor: "transparent" }}
                  onClick={closeModal}
                >
                  <CloseOutlinedIcon />
                </IconButton>
              </Grid>
            </Grid>

            {patchMutation.isError && (
              <Box sx={{ marginBottom: 3 }}>
                <Alert severity="error">
                  {patchMutation.error?.response?.data?.message ||
                    patchMutation.error?.message ||
                    "Failed to update quantity. Please try again."}
                </Alert>
              </Box>
            )}

            {recordList.length === 0 ? (
              <Box sx={{ marginBottom: 4 }}>
                <Alert severity="warning">
                  No editable area records exist for this product on this
                  count.
                </Alert>
              </Box>
            ) : isSingle ? (
              <Box sx={{ marginBottom: 4 }}>
                <Typography variant="h6" sx={{ marginBottom: 2 }}>
                  {recordList[0].areaName
                    ? `${recordList[0].areaName} Count:`
                    : "Total Establishment Count:"}
                </Typography>
                <TextField
                  fullWidth
                  value={quantityById[recordList[0].id] ?? ""}
                  onChange={(e) =>
                    handleQuantityChange(recordList[0].id, e.target.value)
                  }
                  placeholder="0.00"
                  variant="outlined"
                  autoFocus
                  inputProps={{
                    inputMode: "numeric",
                    style: { textAlign: "center", fontSize: "1.2rem" },
                  }}
                  sx={{
                    "& legend": { display: "none" },
                    "& fieldset": { top: 0 },
                    "& .MuiOutlinedInput-root": {
                      background: (theme) => theme.palette.pure.white,
                    },
                    "& input::-webkit-clear-button, & input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button":
                      {
                        display: "none",
                      },
                  }}
                />
              </Box>
            ) : (
              <Box
                sx={{
                  maxHeight: 400,
                  overflow: "auto",
                  border: "1px solid",
                  borderColor: (theme) => theme.palette.terrain[300],
                  borderRadius: 1,
                  marginBottom: 4,
                }}
              >
                <List disablePadding>
                  {recordList.map((r) => {
                    const value = quantityById[r.id] ?? "";
                    const dirty = value !== (initialState[r.id] ?? "");
                    return (
                      <ListItem
                        key={r.id}
                        sx={{
                          backgroundColor: (theme) =>
                            theme.palette.terrain[50],
                          borderBottom: "1px solid",
                          borderColor: (theme) => theme.palette.terrain[300],
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          padding: 2,
                          gap: 2,
                        }}
                      >
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography
                            variant="body1"
                            sx={{ fontWeight: dirty ? 700 : 600 }}
                          >
                            {r.areaName || "Area"}
                          </Typography>
                        </Box>
                        <Box sx={{ flexShrink: 0, minWidth: 100 }}>
                          <TextField
                            size="small"
                            placeholder="0.00"
                            value={value}
                            onChange={(e) =>
                              handleQuantityChange(r.id, e.target.value)
                            }
                            fullWidth
                            sx={{
                              "& legend": { display: "none" },
                              "& fieldset": { top: 0 },
                              "& .MuiOutlinedInput-root": {
                                background: (theme) => theme.palette.pure.white,
                              },
                              "& input": { textAlign: "center" },
                              "& input::-webkit-clear-button, & input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button":
                                {
                                  display: "none",
                                },
                            }}
                            error={value === ""}
                          />
                        </Box>
                      </ListItem>
                    );
                  })}
                </List>
              </Box>
            )}

            <Box sx={{ marginTop: 4 }}>
              <Button
                fullWidth
                variant="contained"
                onClick={handleSave}
                disabled={!canSave || patchMutation.isLoading}
                sx={{ height: "48px" }}
              >
                {isSingle ? "Save Total" : `Save Changes (${dirtyRecords.length})`}
              </Button>
            </Box>
          </>
        )}
      </Box>
    </Modal>
  );
};

export default EditStockCountRecordQuantityModal;
